/* eslint-disable no-shadow */
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
// Components
import {
  Box,
  Button,
  PageHeader,
  Pagination,
  Select,
  Tag,
  Popover,
  TableSkeleton,
  Picto,
  useTable,
  generateTableFromSchema,
  PAGINATION_DEFAULT_NUMBER,
  PAGINATION_DEFAULT_SIZE,
  ScreenModal,
  Textfield,
  Formblock,
  Switch,
} from '@zolteam/zol-admin-ui';
// Permissions
import permissions from '../../../permissions/permissionManagement';
import ProtectedFeature from '../../../permissions/ProtectedFeature';
// Services
import { useGetContentTypeQuery } from '../../../services/cortizol/content-type/contentTypeQueries';
import { useGetAllContentsQuery } from '../../../services/cortizol/content/contentQueries';
import {
  postArchiveContent, postContent, postDuplicateContent, postRestoreContent,
} from '../../../services/cortizol/content/contentService';
// Hooks
import useLocale from '../../../domain/cortizol/locale/useLocale';
import useContent from '../../../domain/cortizol/content/useContent';
import useContentTranslation from '../../../domain/cortizol/content-translation/useContentTranslation';
// Styles
import styles from './ContentTypeList.module.css';

const contentSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  title: 'Contenu',
  additionalProperties: false,
  type: 'object',
  properties: {
    name: {
      type: 'string',
      description: 'Nom interne',
    },
    creationTimestamp: {
      type: 'number',
      format: 'timestamp',
      description: 'Date de création',
    },
    status: {
      type: 'string',
      description: 'Statut',
    },
  },
};

/**
 * ContentTypeList View
 * @returns {JSX.Element}
 */
function ContentTypeList() {
  // Hooks
  const { t } = useTranslation();
  const { contentTypeId } = useParams();
  const navigate = useNavigate();
  const { currentLocale, localesOptions } = useLocale();
  const { CONTENT_STATUS, getContentStatusUIConfig } = useContent();
  const { getContentTranslationStatusUIConfig } = useContentTranslation();
  // -- search-params
  const [searchParam, setSearchParams] = useSearchParams();
  const pageNumber = searchParam.get('pageNumber') || PAGINATION_DEFAULT_NUMBER;
  const pageSize = searchParam.get('pageSize') || PAGINATION_DEFAULT_SIZE;
  const sort = searchParam.get('sort') || 'creationTimestamp';
  const order = searchParam.get('order') || 'desc';
  const contentStatus = /** @type {ContentStatus} */(searchParam.get('status') || 'online');

  const {
    handlePaginationChange,
    handleColumnSort,
    countSelectedRows,
    dispatch: useTableDispatch,
  } = useTable({
    setParams: (params) => setSearchParams((searchParams) => {
      Object.entries(params).forEach(([key, value]) => {
        searchParams.set(key, value);
      });
      return searchParams;
    }),
    order,
    sort,
  });

  // States
  const paginationCountLabel = (
    currentPageMin,
    currentPageMax,
    count,
  ) => t('cortizol.pagination.resultByPage', { currentPageMin, currentPageMax, count });
  const [isNewContentModalOpen, setIsNewContentModalOpen] = useState(false);

  // CONTENT
  const {
    data: dataContent,
    isLoading: isLoadingDataContent,
  } = useGetContentTypeQuery(contentTypeId);
  const {
    data: dataContentList,
    refetch: refetchContentData,
    isLoading: isDataContentListLoading,
  } = useGetAllContentsQuery({
    contentTypeId,
    pageNumber,
    pageSize,
    sort: `${sort}:${order}`,
    status: contentStatus,
  }, {
    keepPreviousData: true,
  });
  const postContentMutation = useMutation(postContent, {
    onSuccess: (res) => {
      setIsNewContentModalOpen(false);

      const newContentId = res.headers.location.split('/').pop();
      navigate(`/content/${contentTypeId}/${newContentId}`);
    },
  });
  const postArchiveContentMutation = useMutation(
    {
      mutationFn: (/** @type {{contentId:string, version:number}} */mutationData) => (
        postArchiveContent(mutationData.contentId, mutationData.version)),
      onSuccess: () => refetchContentData(),
    },

  );
  const postRestoreContentMutation = useMutation(
    {
      mutationFn: (/** @type {{contentId:string, version:number}} */mutationData) => (
        postRestoreContent(mutationData.contentId, mutationData.version)),
      onSuccess: () => refetchContentData(),
    },
  );
  const postDuplicateContentMutation = useMutation(
    {
      mutationFn: (/** @type {{contentId:string}} */mutationData) => (
        postDuplicateContent(mutationData.contentId)),
      onSuccess: () => refetchContentData(),
    },
  );

  // Handlers

  /**
   * Handle new content button click
   * @returns {void}
   */
  const handleNewContentClick = () => setIsNewContentModalOpen(true);
  /**
   *  Handle create content submit
   * @param {React.FormEvent<HTMLFormElement>} e
   * @returns {void}
   */
  const handleCreateContentSubmit = (e) => {
    e.preventDefault();
    // @ts-ignore can't type correctly with JSDOC on e.target form event
    const formData = new FormData(e.target);
    const name = formData.get('name').toString();
    const localeId = formData.get('localId').toString();

    postContentMutation.mutate({
      contentTypeId,
      localeId,
      name,
      data: {},
    });
  };
  /**
   *  Handle click on row to navigate
   * @param {Content} row
   * @returns {void}
   */
  const handleRowNavigate = (row) => navigate(`/content/${row.contentTypeId}/${row.id}`);
  /**
   * Handle archive content click
   * @param {Content} contentToArchive
   * @returns {void}
   */
  const handleArchiveContentClick = (contentToArchive) => {
    postArchiveContentMutation.mutate(
      { contentId: contentToArchive?.id, version: contentToArchive?.version },
    );
    useTableDispatch({ type: 'RESET_SELECTED_ROWS' });
  };
  /**
   * Handle duplicate content click
   * @param {Content} contentToDuplicate
   * @returns {void}
   */
  const handleDuplicateContentClick = (contentToDuplicate) => postDuplicateContentMutation.mutate(
    { contentId: contentToDuplicate.id },
  );
  /**
   * Handle restore content click
   * @param {Content} contentToRestore
   * @returns {void}
   */
  const handleRestoreContentClick = (contentToRestore) => postRestoreContentMutation.mutate(
    { contentId: contentToRestore.id, version: contentToRestore.version },
  );
  /**
   * Handle is archived filter change
   * @param {boolean} switchValue
   * @returns {void}
   */
  const handleIsArchivedFilterChange = (switchValue) => setSearchParams(
    { status: switchValue ? CONTENT_STATUS.archived : CONTENT_STATUS.online },
  );

  return (
    <Box className="padding-l" direction="column" gap="s">
      <PageHeader
        title={(
          <>
            <h1 className="h1-default" style={{ fontWeight: 'bold' }}>
              {`${t('cortizol.contentTypeList.title')} ${dataContent?.schema?.title ? `- ${dataContent?.schema?.title}` : ''}`}
            </h1>
            {dataContentList?.data?.length
            && dataContentList.pagination.recordsCount ? (
              <span className="font-color-neutral-500">
                {t('cortizol.contentTypeList.nbRecords', { count: dataContentList.pagination.recordsCount })}
              </span>
              ) : null}
          </>
        )}
        actions={(
          <ProtectedFeature
            permissions={permissions.contentTypeList.createNewContent}
          >
            <Button icon="add" onClick={handleNewContentClick}>
              {t('cortizol.contentTypeList.createNewContentType')}
            </Button>
          </ProtectedFeature>
        )}
      />

      <Switch
        name="isArchiveFilter"
        id="isArchiveFilter"
        checked={contentStatus === CONTENT_STATUS.archived}
        onChange={handleIsArchivedFilterChange}
        label={t('cortizol.contentTypeList.archiveFilterLabel')}
      />

      {countSelectedRows ? (
        <Box align="center" justify="flex-end" fullWidth>
          {t('cortizol.contentTypeList.nbRecordsSelected', { count: countSelectedRows })}
          <Button color="noBackground" size="small" onClick={() => useTableDispatch({ type: 'RESET_SELECTED_ROWS' })}>
            {t('cortizol.contentTypeList.reset')}
          </Button>
        </Box>
      ) : null}

      {isLoadingDataContent || isDataContentListLoading
        ? <TableSkeleton />
        : generateTableFromSchema(
          {
            JSONSchema: contentSchema,
            data: dataContentList?.data || [],
            sort: {
              order,
              active: sort,
              handleColumnSort,
            },
            navigate: {
              handleRowNavigate,
            },
            limitColumns: 2,
            customColumns: [
              {
                key: 'status',
                description: 'Status',
                position: 'end',
                renderHead: null,
                renderCell: (/** @type {Content} */row) => (
                  <Box className="padding-s">
                    <Tag
                      color={getContentStatusUIConfig(row.status).color}
                      label={t(getContentStatusUIConfig(row.status).label)}
                    />
                  </Box>
                ),
              },
              {
                key: 'translations',
                description: 'Traductions',
                position: 'end',
                renderHead: null,
                renderCell: (/** @type {Content} */row) => (
                  <Box className="padding-s" direction="row" gap="s">
                    {row.translations.map((translation) => (
                      <Tag
                        key={translation.localeId}
                        color={getContentTranslationStatusUIConfig(translation.status).color}
                        label={`${translation.localeId}: ${t(getContentTranslationStatusUIConfig(translation.status).label)}`.toLowerCase()}
                      />
                    ))}
                  </Box>
                ),
              },
              {
                key: 'actions',
                description: '-',
                type: 'custom',
                sortable: false,
                position: 'end',
                renderHead: () => '',
                renderCell: (/** @type {Content} */row) => (
                  <Box justify="center">
                    <Popover
                      minWidth
                      animation="fade"
                      placement="left"
                      offset={[0, 0]}
                      content={(
                        <Box direction="column">
                          <ProtectedFeature
                            permissions={permissions.contentTypeList.duplicateContent}
                          >
                            <Button
                              size="small"
                              color="noBackground"
                              icon="add"
                              onClick={() => handleDuplicateContentClick(row)}
                            >
                              {t('cortizol.contentTypeList.duplicate')}
                            </Button>
                          </ProtectedFeature>
                          <ProtectedFeature
                            permissions={permissions.contentTypeList.archiveRestoreContent}
                          >
                            {row.status === CONTENT_STATUS.archived
                              ? (
                                <Button
                                  size="small"
                                  color="noBackground"
                                  icon="undo"
                                  onClick={() => handleRestoreContentClick(row)}
                                >
                                  {t('cortizol.contentTypeList.restore')}
                                </Button>
                              )
                              : (
                                <Button
                                  size="small"
                                  color="noBackground"
                                  icon="trash"
                                  onClick={() => handleArchiveContentClick(row)}
                                >
                                  {t('cortizol.contentTypeList.delete')}
                                </Button>
                              )}
                          </ProtectedFeature>
                        </Box>
                    )}
                    >
                      <Picto className="font-color-primary-500" icon="more" style={{ height: '22px' }} />
                    </Popover>
                  </Box>
                ),
              },
            ],
            labelNoResult: t('cortizol.contentTypeList.noResult'),
          },
        )}

      {dataContentList?.data?.length && dataContentList?.pagination.recordsCount ? (
        <Pagination
          recordsCount={dataContentList?.pagination?.recordsCount}
          pageNumber={+pageNumber}
          pageSize={+pageSize}
          onPageNumberChange={handlePaginationChange}
          countLabelCallback={paginationCountLabel}
          perPageResultLabel={t('cortizol.pagination.resultPerPage')}
        />
      ) : null}

      <ScreenModal
        isOpen={isNewContentModalOpen}
        onClose={() => setIsNewContentModalOpen(false)}
        className={styles.addModal}
      >
        <form onSubmit={handleCreateContentSubmit} className={styles.addModalForm}>
          <Formblock
            htmlFor="name"
            label={t('cortizol.contentTypeList.addModal.nameLabel')}
            className={styles.addModalFormBlock}
          >
            <Textfield
              id="name"
              type="text"
              name="name"
              placeholder={t('cortizol.contentTypeList.addModal.namePlaceholder')}
            />
          </Formblock>
          <Formblock
            htmlFor="localId"
            label={t('cortizol.contentTypeList.addModal.localLabel')}
            className={styles.addModalFormBlock}
          >
            <Select
              id="localId"
              placeholder={t('cortizol.contentTypeList.addModal.localPlaceholder')}
              options={localesOptions}
              name="localId"
              defaultValue={currentLocale}
              required
            />
          </Formblock>
          <Button
            isLoading={postContentMutation.isLoading}
            isSubmit
          >
            {t('cortizol.contentTypeList.addModal.confirmAction')}
          </Button>
        </form>
      </ScreenModal>
    </Box>
  );
}

export default ContentTypeList;
